import { useState } from 'react';
import axios from 'axios';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  CreateN8NConfigurationParams,
  N8NConfigurationModel,
  UpdateN8NConfigurationParams,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import type { N8nConfigurationFormValues } from '../utils/validation';

export const useN8nConfiguration = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/workflow_engine/n8n`;

  const {
    isLoading,
    isError,
    data: configuration,
    reloadData,
  } = useDataFetcher<N8NConfigurationModel | null>(baseRoute);

  const createConfiguration = async (values: N8nConfigurationFormValues) => {
    try {
      setIsSubmitting(true);
      await axios.post<N8NConfigurationModel>(baseRoute, values as CreateN8NConfigurationParams);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateConfiguration = async (values: N8nConfigurationFormValues) => {
    try {
      setIsSubmitting(true);
      const payload: UpdateN8NConfigurationParams = {
        timeout_seconds: Number(values.timeout_seconds),
        is_enabled: values.is_enabled,
      };

      await axios.put<N8NConfigurationModel>(baseRoute, payload);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isLoading,
    isError,
    configuration,
    isSubmitting,
    hasConfiguration: !!configuration?.id,
    isN8nEnabled: !!configuration?.id && !!configuration?.is_enabled,
    createConfiguration,
    updateConfiguration,
  };
};
